import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography } from '@mui/material';
import { PageBannerAsset } from 'common/interfaces/agg-page';
import { DraggableImageCard } from 'common/ui/draggable-image-card';
import { MuiFileUploader } from 'common/ui/file-uploader/MuiFileUploader';
import { ChangeEvent, FC, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import type { IPageFormV2 } from '../../../page-builder-v2.types';
import { styles } from './banners-section.styles';
import { GalleryPreview } from './gallery-preview';

export interface IVisibleBanner extends PageBannerAsset {
  originalIndex: number;
}

export const BannersSection: FC = () => {
  const { control } = useFormContext<IPageFormV2>();

  const { append, fields, remove, update } = useFieldArray({
    control,
    name: 'pageBanners',
  });

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddNewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleAddBanner(file, URL.createObjectURL(file));
    e.target.value = '';
  };

  const visibleBanners: IVisibleBanner[] = fields
    .map((field, index) => ({ ...field, originalIndex: index }))
    .filter((f) => f.type !== 'deleted');

  const handleAddBanner = (file: File, previewSrc: string) => {
    const newBanner: PageBannerAsset = {
      assets_type: 'assets',
      file_data: { type: 'banner' },
      type: 'new',
      file,
      preview: previewSrc,
    };

    append(newBanner);
  };

  const handleDelete = (originalIndex: number) => {
    const item = fields[originalIndex];
    if (item.type === 'new') {
      remove(originalIndex);
    } else {
      update(originalIndex, { ...item, type: 'deleted' });
    }
  };

  const handleSwap = (visIdx1: number, visIdx2: number) => {
    const origIdx1 = visibleBanners[visIdx1].originalIndex;
    const origIdx2 = visibleBanners[visIdx2].originalIndex;
    const item1 = fields[origIdx1];
    const item2 = fields[origIdx2];
    update(origIdx1, { ...item2, type: item2.type === 'new' ? 'new' : 'renew' });
    update(origIdx2, { ...item1, type: item1.type === 'new' ? 'new' : 'renew' });
  };

  const handleDrop = (dropIndex: number) => {
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      handleSwap(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <Accordion
      defaultExpanded
      disableGutters
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        boxShadow: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': { margin: '12px 0' },
          '& .MuiAccordionSummary-content.Mui-expanded': { margin: '12px 0' },
          '&.Mui-expanded': { minHeight: 52 },
          minHeight: 52,
          px: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mr: 1 }}>
          <Typography fontWeight={600} variant="subtitle1">Page Gallery</Typography>
          {visibleBanners.length > 0 && (
            <Button onClick={handleAddNewClick} size="small" startIcon={<AddIcon />} variant="outlined">
              Add New
            </Button>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          p: visibleBanners.length === 0 ? 0 : 2.5,
        }}
      >
        <input
          ref={fileInputRef}
          accept="image/jpeg, image/png, image/jpg, image/svg"
          hidden
          onChange={handleFileChange}
          type="file"
        />
        {visibleBanners.length === 0 ? (
          <Box
            sx={{
              '& > *': {
                borderRadius: '0 0 8px 8px !important',
                border: 'none !important',
                height: '220px !important',
              },
            }}
          >
            <MuiFileUploader
              format={['image/jpeg', 'image/png', 'image/jpg', 'image/svg']}
              onChange={handleAddBanner}
              withoutPreview
            />
          </Box>
        ) : (
          <>
            <GalleryPreview onAddBanner={handleAddBanner} visibleBanners={visibleBanners} />

            <Box sx={styles.listContainer}>
              {visibleBanners.map((banner, index) => (
                <DraggableImageCard
                  key={banner.id || banner.preview || index}
                  dragOverIndex={dragOverIndex}
                  height={200}
                  index={index}
                  onDelete={() => handleDelete(banner.originalIndex)}
                  onDragLeave={() => setDragOverIndex(null)}
                  onDragOver={setDragOverIndex}
                  onDragStart={setDraggedIndex}
                  onDrop={handleDrop}
                  onSwap={handleSwap}
                  src={banner.url || banner.preview || ''}
                  totalItems={visibleBanners.length}
                  width={200}
                />
              ))}
            </Box>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
